import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CreditCard, CheckCircle2, Clock, AlertTriangle, ExternalLink, Star } from "lucide-react";

const FEATURE_LABELS = {
  starter: [
    "Up to 3 active drivers",
    "100 trips / month",
    "Branded PDF pay statements",
    "Email support",
  ],
  growth: [
    "Up to 10 active drivers",
    "Unlimited trips",
    "QuickBooks CSV export",
    "Custom fields",
    "Audit trail on every record",
    "Email support",
  ],
  pro: [
    "Unlimited drivers",
    "Unlimited trips",
    "Everything in Growth",
    "Driver portal (mobile pay stubs)",
    "White-label branding",
    "Priority phone + SMS support",
  ],
};

export default function Billing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState("growth");

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/billing/status"),
        api.get("/plans"),
      ]);
      setState(s.data);
      setPlans(p.data.plans);
      if (s.data?.plan_key) setPicked(s.data.plan_key);
    } catch (e) {
      toast.error(errMsg(e, "Could not load billing"));
    } finally {
      setLoading(false);
    }
  };

  if (user?.role === "superadmin") return <Navigate to="/admin/workspaces" replace />;

  const startCheckout = async (planKey, skipTrial = false) => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        plan: planKey,
        skip_trial: skipTrial,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not open checkout"));
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not open billing portal"));
      setBusy(false);
    }
  };

  if (loading || !state) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading billing...</div>;
  }

  const subStatus = state.subscription_status;
  const hasActive = state.has_active_subscription || subStatus === "past_due" || subStatus === "canceled";
  const nice = {
    trialing: ["Trial active", "bg-amber-50 text-amber-800 border-amber-200"],
    active: ["Active subscription", "bg-emerald-50 text-emerald-800 border-emerald-200"],
    past_due: ["Payment past due", "bg-rose-50 text-rose-800 border-rose-200"],
    canceled: ["Canceled", "bg-slate-100 text-slate-700 border-slate-200"],
  }[subStatus] || null;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Billing</h1>
            <p className="text-sm text-slate-500">{state.company_name}</p>
          </div>
          <Button variant="outline" onClick={() => nav("/")} data-testid="back-to-app-btn">Back to app</Button>
        </div>

        {/* Current state card */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 mb-5" data-testid="billing-current">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded bg-slate-900 grid place-items-center text-white"><CreditCard size={20} /></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">Current plan</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {state.plan_name || "No plan"}
                <span className="text-sm font-normal text-slate-500"> · ${(state.price_cents/100).toFixed(0)}/mo</span>
              </div>
              {nice ? (
                <span className={`inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded border ${nice[1]}`}>{nice[0]}</span>
              ) : (
                <span className="inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-200">No subscription</span>
              )}
              {state.current_period_end && (
                <div className="text-xs text-slate-500 mt-3">
                  {subStatus === "canceled" ? "Access until " : "Renews on "} 
                  {new Date(state.current_period_end).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trial banner */}
        {!state.has_active_subscription && !state.trial_expired && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 mb-5 flex items-start gap-3" data-testid="trial-banner">
            <Clock className="text-amber-700 shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                {state.trial_days_left === 0 ? "Trial ends today" : `Trial ends in ${state.trial_days_left} day${state.trial_days_left === 1 ? "" : "s"}`}
              </div>
              <div className="text-sm text-amber-800">
                Pick a plan below to keep every driver, trip and statement after the trial ends.
              </div>
            </div>
          </div>
        )}

        {/* Paywall banner */}
        {state.paywall && (
          <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-5 mb-5 flex items-start gap-3" data-testid="paywall-banner">
            <AlertTriangle className="text-rose-700 shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <div className="font-semibold text-rose-900">Trial expired — pick a plan to restore access</div>
              <div className="text-sm text-rose-800">
                Your data is safe. Start a subscription below to unlock dispatch and payroll again.
              </div>
            </div>
          </div>
        )}

        {hasActive ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <div className="text-sm font-semibold text-slate-900 mb-1">Manage billing</div>
            <div className="text-sm text-slate-500 mb-4">
              Switch plans, update your card, download invoices, or cancel — all on Stripe's hosted portal.
            </div>
            <Button
              onClick={openPortal}
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="open-portal-btn"
            >
              {busy ? "Opening…" : (<>Manage billing <ExternalLink size={14} className="ml-1" /></>)}
            </Button>
          </div>
        ) : (
          <>
            <div className="text-sm font-semibold text-slate-900 mb-3">Pick a plan</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plan-picker">
              {plans.map((p) => {
                const isPicked = picked === p.key;
                const isPopular = p.popular;
                return (
                  <div
                    key={p.key}
                    data-testid={`plan-card-${p.key}`}
                    onClick={() => setPicked(p.key)}
                    className={`relative rounded-lg border bg-white p-5 cursor-pointer transition
                      ${isPicked ? "border-blue-600 ring-2 ring-blue-100 shadow-sm" : "border-slate-200 hover:border-slate-300"}
                    `}
                  >
                    {isPopular && (
                      <span className="absolute -top-2 right-4 bg-amber-500 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                        <Star size={10} fill="currentColor" /> Most popular
                      </span>
                    )}
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="mt-2 flex items-end gap-1">
                      <div className="text-3xl font-bold text-slate-900">${(p.price_cents/100).toFixed(0)}</div>
                      <div className="text-slate-500 mb-1 text-sm">/ mo</div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{p.tagline}</div>
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                      {(FEATURE_LABELS[p.key] || []).map((f) => (
                        <li key={f} className="flex gap-2">
                          <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 rounded-lg border border-slate-200 bg-white p-5">
              <Button
                onClick={() => startCheckout(picked, false)}
                disabled={busy}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="start-checkout-btn"
              >
                {busy ? "Opening Stripe…" : (() => {
                  const sel = plans.find((p) => p.key === picked);
                  const dollars = sel ? (sel.price_cents/100).toFixed(0) : "–";
                  const name = sel ? sel.name : "";
                  return state.trial_days_left > 0
                    ? `Subscribe to ${name} · $${dollars}/mo (${state.trial_days_left}d trial)`
                    : `Subscribe to ${name} · $${dollars}/mo`;
                })()}
              </Button>
              {state.trial_days_left > 0 && (
                <div className="mt-3">
                  <Button
                    onClick={() => startCheckout(picked, true)}
                    disabled={busy}
                    variant="outline"
                    className="text-slate-700"
                    data-testid="start-checkout-now-btn"
                  >
                    Skip trial & bill me today
                  </Button>
                  <div className="text-[11px] text-slate-500 mt-2">
                    "Skip trial" charges your card for the first month immediately — useful for testing the live checkout flow end-to-end.
                  </div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 mt-3">
                You'll be redirected to Stripe's hosted checkout. Test card: <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
